import React, { memo, useMemo } from 'react';
import { Clock, ChevronRight } from 'lucide-react';
import { Service } from '../../types';
import { BloomBadge } from '../UI/Bloom';
import { EmptyState } from '../UI/Bloom/flow';
import { T, rgba } from '../../utils/adminTheme';

interface ServiceSelectionProps {
  services: Service[];
  selectedService: Service | null;
  onServiceSelect: (service: Service) => void;
  popularIds?: number[];
}

const ServiceSelection = memo(({ services, selectedService, onServiceSelect, popularIds = [] }: ServiceSelectionProps) => {
  const servicesByCategory = useMemo(() => {
    const grouped = services.reduce((acc, service) => {
      const category = service.category || 'Services';
      if (!acc[category]) acc[category] = [];
      acc[category].push(service);
      return acc;
    }, {} as Record<string, Service[]>);

    return Object.entries(grouped);
  }, [services]);

  if (services.length === 0) {
    return (
      <EmptyState
        title="Keine Services verfügbar"
        hint="Bitte Supabase-Verbindung oder Service-Stammdaten prüfen."
      />
    );
  }

  return (
    <div style={{ display: 'grid', gap: 30 }}>
      {servicesByCategory.map(([category, categoryServices]) => (
        <section key={category}>
          <div style={{
            display: 'flex',
            alignItems: 'baseline',
            justifyContent: 'space-between',
            gap: 12,
            marginBottom: 14,
          }}>
            <h2 style={{
              margin: 0,
              fontFamily: T.display,
              fontStyle: 'italic',
              fontSize: 27,
              fontWeight: 500,
              color: T.text,
            }}>
              {category}
            </h2>
            <div style={{
              flex: 1,
              height: 1,
              background: T.line,
              minWidth: 48,
            }} />
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: 12,
          }}>
            {categoryServices.map(service => {
              const isSelected = selectedService?.id === service.id;
              const isPopular = popularIds.includes(Number(service.id));

              return (
                <button
                  key={service.id}
                  onClick={() => onServiceSelect(service)}
                  style={{
                    textAlign: 'left',
                    background: isSelected ? T.surface2 : T.surface,
                    border: `1px solid ${isSelected ? T.accent : T.line}`,
                    borderRadius: T.r.lg,
                    padding: 0,
                    overflow: 'hidden',
                    cursor: 'pointer',
                    color: T.text,
                    boxShadow: isSelected ? `0 14px 34px ${rgba(T.accent, 0.18)}` : 'none',
                    transition: 'border .18s, box-shadow .18s, transform .18s',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  {/* Bild */}
                  {service.image_url && service.image_visible && (
                    <img
                      src={service.image_url}
                      alt={service.name}
                      style={{
                        width: '100%',
                        height: 120,
                        objectFit: 'cover',
                        display: 'block',
                      }}
                    />
                  )}
                  <div style={{ padding: 18, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', gap: 14, flex: 1 }}>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      gap: 12,
                    }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                        {isPopular && (
                          <BloomBadge fg="#b45309" bg="rgba(180,83,9,0.1)">
                            ✦ Beliebt
                          </BloomBadge>
                        )}
                      <h3 style={{
                        margin: 0,
                        fontFamily: T.body,
                        fontSize: 16,
                        fontWeight: 600,
                        lineHeight: 1.3,
                      }}>
                        {service.name}
                      </h3>
                      </div>
                      <div style={{
                        fontFamily: T.display,
                        fontStyle: 'italic',
                        fontSize: 24,
                        whiteSpace: 'nowrap',
                      }}>
                        CHF {service.price}
                      </div>
                    </div>

                    {service.description && (
                      <p style={{
                        margin: '10px 0 0',
                        color: T.muted,
                        fontFamily: T.body,
                        fontSize: 13,
                        lineHeight: 1.45,
                      }}>
                        {service.description}
                      </p>
                    )}
                  </div>

                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 10,
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <span style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 5,
                        color: T.muted,
                        fontFamily: T.body,
                        fontSize: 12,
                      }}>
                        <Clock size={14} />
                        {service.duration} Min.
                      </span>
                      {service.prepayment && (
                        <BloomBadge fg={T.warn} bg={rgba(T.warn, 0.14)}>
                          Anzahlung CHF {service.prepayment_amount}
                        </BloomBadge>
                      )}
                    </div>
                    <ChevronRight size={17} color={isSelected ? T.accent : T.faint} />
                  </div>
                </button>
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );
});

ServiceSelection.displayName = 'ServiceSelection';

export default ServiceSelection;
